/**
 * Category Display Name Formatter
 * Converts database category values (hair-care) to display names (Hair Care)
 */

/**
 * Convert a category slug to a display name
 * @param {string} category - Category slug (e.g., "hair-care", "nail-services")
 * @returns {string} - Display name (e.g., "Hair Care", "Nail Services")
 */
export const formatCategoryName = (category) => {
  if (!category) return "";

  // Split by hyphen or underscore
  const words = category.split(/[-_]/).map((word) => {
    // Capitalize first letter of each word
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return words.join(" ");
};

/**
 * Format multiple categories with separator
 * @param {string|string[]} categories - Single category or array of categories
 * @param {string} separator - Separator between categories (default: ", ")
 * @returns {string} - Formatted category string
 */
export const formatCategories = (categories, separator = ", ") => {
  if (!categories) return "";

  // Handle string or array
  const categoryArray = Array.isArray(categories)
    ? categories
    : typeof categories === "string"
      ? categories.split(",").map((c) => c.trim())
      : [];

  return categoryArray.map(formatCategoryName).join(separator);
};

/**
 * Common category mappings for edge cases
 */
const CATEGORY_OVERRIDES = {
  "spa-services": "Spa Services",
  "massage-therapy": "Massage Therapy",
  "facial-treatments": "Facial Treatments",
  "body-treatments": "Body Treatments",
  "hair-styling": "Hair Styling",
  "hair-color": "Hair Color",
  "hair-care": "Hair Care",
  "nail-care": "Nail Care",
  "nail-services": "Nail Services",
  "makeup-services": "Makeup Services",
  "skin-care": "Skin Care",
  "beauty-services": "Beauty Services",
  barbering: "Barbering",
  braiding: "Braiding",
  extensions: "Extensions",
  "lash-services": "Lash Services",
  "brow-services": "Brow Services",
  waxing: "Waxing",
  "tattoo-piercing": "Tattoo & Piercing",
  wellness: "Wellness",
  fitness: "Fitness",
};

/**
 * Get display name with override support
 * @param {string} category - Category slug
 * @returns {string} - Display name
 */
export const getCategoryDisplayName = (category) => {
  if (!category) return "";

  const normalized = category.toLowerCase().trim();
  return CATEGORY_OVERRIDES[normalized] || formatCategoryName(category);
};
